import React from 'react';
import {toast} from 'react-toastify';
import {SubmitHandler, useForm} from 'react-hook-form';
import {t} from '@lingui/core/macro';

import {EditTariffVariantType, tariffVariantAPI} from 'features/admin/api/tariffVariantAPI';
import {tariffAPI, ITariffVariant} from 'entities/tariff';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import ModalBlockButton from 'shared/ui/Button/ModalBlockButton/ModalBlockButton';
import InputNumber from 'shared/ui/Input/InputNumber/InputNumber';
import Checkbox from 'shared/ui/Checkbox/Checkbox';
import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';

import styles from './TariffVariantForm.module.scss';

interface ITariffVariantFormProps {
    onSuccess: () => void;
    item?: ITariffVariant;
    onClose: () => void;
}

interface FormData {
    period: number;
    discount: number;
    tariff_ids: number[];
}

const TariffVariantForm: React.FC<ITariffVariantFormProps> = ({onSuccess, item, onClose}) => {
    const [addTariffVariant] = tariffVariantAPI.useAddTariffVariantMutation();
    const [updateTariffVariant] = tariffVariantAPI.useUpdateTariffVariantMutation();
    const {data: tariffs} = tariffAPI.useGetTariffsQuery(undefined);

    const {handleSubmit, formState, control, setValue, watch} = useForm<FormData>({
        mode: 'onChange',
        defaultValues: {
            period: item?.period ?? 0,
            discount: item?.discount ?? 0,
            tariff_ids: item?.tariffs.map(t => t.id) ?? [],
        },
    });


    const handleCheckboxChange = (tariffId: number) => {
        const newTariffs = selectedTariffs.includes(tariffId)
            ? selectedTariffs.filter(id => id !== tariffId)
            : [...selectedTariffs, tariffId];
        setValue('tariff_ids', newTariffs);
    };

    const selectedTariffs = watch('tariff_ids') || [];
    const {errors, isValid} = formState;
    const isNew = !item;

    const onSubmit: SubmitHandler<FormData> = async(data) => {
        try {
            const selectedTariffIris = data.tariff_ids.map(id => `/api/v1/tariffs/${id}`);

            const formData: EditTariffVariantType = {
                period: Number(data.period),
                discount: Number(data.discount),
                tariffs: selectedTariffIris,
            };

            if (isNew) {
                await addTariffVariant(formData).unwrap();
                toast.info(t`Вариант тарифа успешно добавлен`);
            } else {
                await updateTariffVariant({
                    id: item.id,
                    ...formData,
                }).unwrap();
                toast.info(t`Вариант тарифа успешно обновлен`);
            }
            setTimeout(onSuccess, 1000);
        } catch (error) {
            const message = error instanceof Error
                ? error.message
                : t`Произошла ошибка при отправке`;
            toast.error(message);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <ModalBodyWrapper>
                <FormGroupWithError
                    error={errors?.discount?.message}
                    label="Скидка, %"
                >
                    <InputNumber
                        min={0}
                        max={100}
                        name="discount"
                        defaultValue={item?.discount ?? 0}
                        control={control}
                        setValue={setValue}
                    />
                </FormGroupWithError>

                <FormGroupWithError
                    error={errors?.period?.message}
                    label="Количество дней"
                >
                    <InputNumber
                        min={0}
                        name="period"
                        defaultValue={item?.period ?? 0}
                        control={control}
                        setValue={setValue}
                    />
                </FormGroupWithError>

                <FormGroupWithError label="Тарифы" >
                    <div className={styles.blockCheckbox}>
                        {tariffs?.map((tariff) => (
                            <div key={tariff.id}>
                                <Checkbox
                                    textStyle={styles.checkbox}
                                    checked={selectedTariffs.includes(tariff.id)}
                                    onChange={() => handleCheckboxChange(tariff.id)}
                                >
                                    {tariff.name}{tariff.is_archive && ' (арх.)'}
                                </Checkbox>
                            </div>
                        ))}
                    </div>
                </FormGroupWithError>
            </ModalBodyWrapper>
            <ModalBlockButton
                primaryButtonText={isNew ? 'Создать' : 'Сохранить'}
                primaryButtonType="submit"
                primaryButtonDisabled={!isValid}
                secondaryButtonOnClick={onClose}
            />
        </form>
    );
};

export default TariffVariantForm;